<!--
  The vertical split of WorkspaceDetails.vue:142-245: the canvas on top (the default slot), the Sequences menu below
  it behind a 6px gutter, at least 200px and at most half the height, the canvas keeping the rest. While the menu is
  collapsed the canvas shows the Sequences pill at the bottom right. PrimeVue's Splitter only reads its panels when it
  mounts, so the gutter here is a small pointer-drag of our own; the height is kept per browser.
-->
<script setup lang="ts">
import { computed, ref } from 'vue';
import { useElementSize, useEventListener, useLocalStorage } from '@vueuse/core';
import Button from 'primevue/button';
import { useLiteStore } from '@nanome-lite/state';
import { useSequenceStore } from '@/sequence/store';
import WorkspaceSequenceMenu from '@/components/WorkspaceSequenceMenu.vue';

// `pillClass` places the pill vertically: the page lifts it above the prompt bar's input row when that bar is shown.
const props = withDefaults(defineProps<{ enabled?: boolean; pillClass?: string }>(), { enabled: true, pillClass: 'bottom-2' });

const store = useLiteStore();
const seq = useSequenceStore();

const GUTTER = 6;
const MIN = 200;
const root = ref<HTMLElement | null>(null);
const { height: rootHeight } = useElementSize(root);
const stored = useLocalStorage<number>('nanome-lite-device.sequenceHeight', 240);

const showSequence = computed(() => props.enabled && !seq.isCollapsed);
const height = computed(() => {
  const max = Math.max(MIN, (rootHeight.value - GUTTER) / 2);
  return Math.round(Math.min(max, Math.max(MIN, stored.value)));
});

let drag: { startY: number; startHeight: number } | null = null;
function startResize(e: PointerEvent) {
  drag = { startY: e.clientY, startHeight: height.value };
  (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
  e.preventDefault();
}
useEventListener(window, 'pointermove', (e: PointerEvent) => {
  if (!drag) return;
  stored.value = drag.startHeight - (e.clientY - drag.startY);
});
useEventListener(window, ['pointerup', 'pointercancel'], () => {
  if (drag) stored.value = height.value;
  drag = null;
});
</script>

<template>
  <div ref="root" class="min-w-0 flex flex-col">
    <div class="relative min-h-0 flex-1 flex">
      <slot />
      <div v-if="props.enabled && seq.isCollapsed" class="absolute right-2 z-10 bg-xr rd-full p-1" :class="props.pillClass">
        <Button
          icon="i-tabler-mist"
          label="Sequences"
          size="small"
          severity="secondary"
          rounded
          text
          :disabled="!store.entries.length"
          data-testid="sequences-pill"
          @click="seq.isCollapsed = false"
        />
      </div>
    </div>
    <template v-if="showSequence">
      <div
        class="shrink-0 flex items-center justify-center bg-section cursor-row-resize touch-none"
        :style="{ height: `${GUTTER}px` }"
        role="separator"
        aria-orientation="horizontal"
        aria-label="Resize the sequence view"
        @pointerdown="startResize"
      >
        <div class="w-8 h-0.5 rd-full bg-[var(--text-muted-color)] op-50" />
      </div>
      <div class="shrink-0 min-h-0" :style="{ height: `${height}px` }">
        <WorkspaceSequenceMenu />
      </div>
    </template>
  </div>
</template>
